import { useApolloClient } from '@apollo/client';
import { validateTopicGraph } from '@arnold/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArielAiChatMessageInput, ArielAiChatRole, ArielAiNodeDataInput } from '../../generated/hooks';
import i18n from '../../translations/i18n';
import { arielAiChatSubscription } from '../../graphql/subscriptions';
import {
  ArielAiChatAttachment,
  attachmentToDisplay,
  ArielAiChatAttachmentDisplay,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from './arielAiAttachments';
import { gqlGraphToNodeData, nodeDataToGqlGraph } from './arielAiGraph';
import { NodeData } from './dataNode';

export type ArielAiChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  attachments?: ArielAiChatAttachmentDisplay[];
};

export type ArielAiChatResult = {
  patch: NodeData[] | null;
  error?: string;
  validationErrors?: string[];
};

type StoredUserMessage = ArielAiChatMessage & {
  attachmentPayload?: ArielAiChatAttachment[];
};

type UseArielAiChatOptions = {
  topicId: string;
  languageCode: string;
  getGraph: () => NodeData[];
  onBeforeSend?: () => void | Promise<void>;
  onComplete?: (result: ArielAiChatResult) => void | Promise<void>;
};

/** Explanation text is streamed before nodes JSON; show preparing after text stalls. */
const STREAM_STALL_MS = 400;
/** Keep applying indicator visible long enough to read. */
const MIN_APPLY_INDICATOR_MS = 500;

const toGqlMessage = (message: StoredUserMessage | ArielAiChatMessage): ArielAiChatMessageInput => {
  if (message.role === 'assistant') {
    return {
      role: ArielAiChatRole.Assistant,
      content: message.content,
    };
  }

  const stored = message as StoredUserMessage;
  return {
    role: ArielAiChatRole.User,
    content: message.content,
    attachments: stored.attachmentPayload?.map(({ filename, mimeType, dataBase64 }) => ({
      filename,
      mimeType,
      dataBase64,
    })),
  };
};

/** Yield so React can paint state updates (e.g. isApplyingPatch) before heavy sync work. */
const waitForPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const waitMs = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const useArielAiChat = ({
  topicId,
  languageCode,
  getGraph,
  onBeforeSend,
  onComplete,
}: UseArielAiChatOptions) => {
  const client = useApolloClient();
  const [messages, setMessages] = useState<StoredUserMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPreparingPatch, setIsPreparingPatch] = useState(false);
  const [isApplyingPatch, setIsApplyingPatch] = useState(false);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const requestIdRef = useRef(0);
  const requestStartedAtRef = useRef(0);
  const preparingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStreamingTextRef = useRef('');
  const chunkCountRef = useRef(0);

  const log = useCallback((event: string, data?: Record<string, unknown>) => {
    // eslint-disable-next-line no-console
    console.log(
      `[Ariel AI #${requestIdRef.current}] +${Math.round(performance.now() - requestStartedAtRef.current)}ms ${event}`,
      data ?? '',
    );
  }, []);

  useEffect(() => {
    if (!isStreaming || !streamingText || isApplyingPatch) {
      if (!isStreaming || !streamingText) {
        setIsPreparingPatch(false);
        if (preparingTimeoutRef.current) {
          clearTimeout(preparingTimeoutRef.current);
          preparingTimeoutRef.current = null;
        }
        lastStreamingTextRef.current = '';
      }
      return;
    }

    if (streamingText === lastStreamingTextRef.current) {
      return;
    }

    lastStreamingTextRef.current = streamingText;
    setIsPreparingPatch(false);

    if (preparingTimeoutRef.current) {
      clearTimeout(preparingTimeoutRef.current);
    }

    preparingTimeoutRef.current = setTimeout(() => {
      log('streaming text stalled — showing preparing indicator', { textLength: streamingText.length });
      setIsPreparingPatch(true);
    }, STREAM_STALL_MS);

    return () => {
      if (preparingTimeoutRef.current) {
        clearTimeout(preparingTimeoutRef.current);
        preparingTimeoutRef.current = null;
      }
    };
  }, [isApplyingPatch, isStreaming, log, streamingText]);

  const cancelSubscription = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
  }, []);

  // Tear down any in-flight request when the component unmounts or when the topic/language changes.
  // Without this the Apollo subscription leaks and its next/error handlers call setState on an
  // unmounted component; on a language/topic switch a late-completing stream could also apply a
  // stale patch to the current canvas.
  useEffect(() => {
    return () => {
      cancelSubscription();
      setIsStreaming(false);
      setIsPreparingPatch(false);
      setIsApplyingPatch(false);
      setStreamingText('');
    };
  }, [cancelSubscription, languageCode, topicId]);

  const sendMessage = useCallback(
    (content: string, attachments: ArielAiChatAttachment[] = []) => {
      const trimmed = content.trim();
      if ((!trimmed && attachments.length === 0) || isStreaming || isApplyingPatch) {
        return;
      }
      if (attachments.length > MAX_ATTACHMENTS_PER_MESSAGE) {
        return;
      }

      cancelSubscription();

      const userMessage: StoredUserMessage = {
        role: 'user',
        content: trimmed,
        attachments: attachments.map(attachmentToDisplay),
        attachmentPayload: attachments,
      };
      const nextMessages = [...messages, userMessage];
      setMessages(nextMessages);
      setStreamingText('');
      setIsPreparingPatch(false);
      setIsStreaming(true);

      void Promise.resolve(onBeforeSend?.())
        .then(() => {
          requestIdRef.current += 1;
          requestStartedAtRef.current = performance.now();
          chunkCountRef.current = 0;
          log('sendMessage', { contentLength: trimmed.length, attachmentCount: attachments.length });

          const gqlMessages: ArielAiChatMessageInput[] = nextMessages.map(toGqlMessage);
          const graph: ArielAiNodeDataInput[] = nodeDataToGqlGraph(getGraph());

          subscriptionRef.current = client
            .subscribe({
              query: arielAiChatSubscription,
              variables: { topicId, languageCode, messages: gqlMessages, graph },
              fetchPolicy: 'network-only',
            })
            .subscribe({
              next: ({ data }) => {
                const chunk = data?.arielAiChat;
                if (!chunk) {
                  return;
                }

                chunkCountRef.current += 1;
                log('chunk received', {
                  chunkIndex: chunkCountRef.current,
                  done: chunk.done,
                  textLength: chunk.text?.length ?? 0,
                  hasPatch: Boolean(chunk.patch),
                  patchNodeCount: chunk.patch?.length ?? 0,
                });

                setStreamingText(chunk.text);

                if (!chunk.done) {
                  return;
                }

                cancelSubscription();
                log('chunk done — finalizing response');

                let result: ArielAiChatResult = { patch: null };

                if (chunk.error) {
                  result = {
                    patch: null,
                    error: chunk.error.message,
                    validationErrors: chunk.error.validationErrors ?? undefined,
                  };
                } else if (chunk.patch) {
                  const patchNodes = gqlGraphToNodeData(chunk.patch);
                  const questionNodes = patchNodes.filter((n: NodeData) => n.type !== 'comment');
                  const validationErrors = validateTopicGraph(questionNodes, questionNodes);
                  if (validationErrors.length > 0) {
                    result = {
                      patch: null,
                      error: i18n.t('topicEditor:arielAiPatchValidationFailed'),
                      validationErrors: validationErrors.map(
                        (e) => `Question ${e.questionIndex} (${e.part}): ${e.error}`,
                      ),
                    };
                  } else {
                    result = { patch: patchNodes };
                  }
                }

                log('patch resolved', {
                  hasPatch: Boolean(result.patch),
                  patchNodeCount: result.patch?.length ?? 0,
                  error: result.error,
                });

                const assistantContent =
                  result.error && result.validationErrors?.length
                    ? `${chunk.text}\n\n${result.error}\n${result.validationErrors.join('\n')}`
                    : result.error
                      ? `${chunk.text}\n\n${result.error}`
                      : chunk.text;

                void (async () => {
                  if (result.patch) {
                    setIsPreparingPatch(false);
                    log('isApplyingPatch=true (keeping streaming text visible until apply completes)');
                    setIsApplyingPatch(true);
                    await waitForPaint();
                    log('paint yielded, starting onComplete');
                    const applyStartedAt = performance.now();
                    try {
                      await onComplete?.(result);
                      log('onComplete finished', { durationMs: Math.round(performance.now() - applyStartedAt) });
                    } finally {
                      const applyElapsed = performance.now() - applyStartedAt;
                      if (applyElapsed < MIN_APPLY_INDICATOR_MS) {
                        log('extending apply indicator', {
                          waitMs: Math.round(MIN_APPLY_INDICATOR_MS - applyElapsed),
                        });
                        await waitMs(MIN_APPLY_INDICATOR_MS - applyElapsed);
                      }
                      log('isApplyingPatch=false');
                      setIsApplyingPatch(false);
                    }
                    setMessages((prev) => [...prev, { role: 'assistant', content: assistantContent }]);
                    setStreamingText('');
                  } else {
                    log('no patch — skipping apply phase');
                    setIsPreparingPatch(false);
                    setMessages((prev) => [...prev, { role: 'assistant', content: assistantContent }]);
                    setStreamingText('');
                    await onComplete?.(result);
                  }
                  setIsStreaming(false);
                  log('request finished');
                })();
              },
              error: (err) => {
                log('subscription error', { message: err.message });
                setIsStreaming(false);
                setIsPreparingPatch(false);
                cancelSubscription();
                const errorMessage = err.message ?? i18n.t('topicEditor:arielAiSubscriptionFailed');
                setMessages((prev) => [...prev, { role: 'assistant', content: errorMessage }]);
                setStreamingText('');
                onComplete?.({ patch: null, error: errorMessage });
              },
            });
        })
        .catch((err) => {
          log('onBeforeSend failed', { message: (err as Error).message });
          setIsStreaming(false);
          setIsPreparingPatch(false);
          const errorMessage = (err as Error).message ?? i18n.t('topicEditor:arielAiSubscriptionFailed');
          setMessages((prev) => [...prev, { role: 'assistant', content: errorMessage }]);
          setStreamingText('');
          onComplete?.({ patch: null, error: errorMessage });
        });
    },
    [
      cancelSubscription,
      client,
      getGraph,
      isApplyingPatch,
      isStreaming,
      languageCode,
      log,
      messages,
      onBeforeSend,
      onComplete,
      topicId,
    ],
  );

  const clearMessages = useCallback(() => {
    cancelSubscription();
    setMessages([]);
    setStreamingText('');
    setIsStreaming(false);
    setIsPreparingPatch(false);
    setIsApplyingPatch(false);
  }, [cancelSubscription]);

  const displayMessages: ArielAiChatMessage[] = messages.map(({ role, content, attachments }) => ({
    role,
    content,
    attachments,
  }));

  return {
    messages: displayMessages,
    streamingText,
    isStreaming,
    isPreparingPatch,
    isApplyingPatch,
    sendMessage,
    clearMessages,
    cancelSubscription,
  };
};
